
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Currency } from '@/lib/types';
import { useCurrencies, CurrencyFormData } from '@/hooks/useCurrencies';
import { useTranslation } from 'react-i18next';

const currencyFormSchema = z.object({
  code: z.string().min(1, 'Currency code is required').max(3, 'Currency code should be at most 3 characters'),
  name: z.string().min(1, 'Currency name is required'),
  symbol: z.string().min(1, 'Currency symbol is required'),
});

export type CurrencyFormValues = z.infer<typeof currencyFormSchema>;

interface CurrencyFormProps {
  currency?: Currency;
  onSuccess?: (currency: Currency) => void;
  onCancel?: () => void;
}

export function CurrencyForm({ currency, onSuccess, onCancel }: CurrencyFormProps) {
  const { t } = useTranslation();
  const { useCreateCurrencyMutation, useUpdateCurrencyMutation } = useCurrencies();
  const createCurrencyMutation = useCreateCurrencyMutation();
  const updateCurrencyMutation = useUpdateCurrencyMutation();

  const form = useForm<CurrencyFormValues>({
    resolver: zodResolver(currencyFormSchema),
    defaultValues: currency
      ? {
          code: currency.code,
          name: currency.name,
          symbol: currency.symbol,
        }
      : {
          code: '',
          name: '',
          symbol: '',
        },
  });

  const onSubmit = async (data: CurrencyFormValues) => {
    try {
      if (currency) {
        // We know all fields are required by the form validation, so we can safely cast
        const formData: CurrencyFormData = {
          code: data.code,
          name: data.name,
          symbol: data.symbol
        };
        
        const updatedCurrency = await updateCurrencyMutation.mutateAsync({
          id: currency.id,
          data: formData,
        });
        if (onSuccess) onSuccess(updatedCurrency);
      } else {
        // We know all fields are required by the form validation, so we can safely cast
        const formData: CurrencyFormData = {
          code: data.code,
          name: data.name,
          symbol: data.symbol
        };
        
        const newCurrency = await createCurrencyMutation.mutateAsync(formData);
        if (onSuccess) onSuccess(newCurrency);
      }
    } catch (error) {
      console.error('Error saving currency:', error);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('currencyCode')}</FormLabel>
              <FormControl>
                <Input placeholder="USD" {...field} maxLength={3} />
              </FormControl>
              <FormDescription>
                {t('currencyCodeDescription')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('currencyName')}</FormLabel>
              <FormControl>
                <Input placeholder="US Dollar" {...field} />
              </FormControl>
              <FormDescription>
                {t('currencyNameDescription')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="symbol"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('currencySymbol')}</FormLabel>
              <FormControl>
                <Input placeholder="$" {...field} />
              </FormControl>
              <FormDescription>
                {t('currencySymbolDescription')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2 pt-4">
          {onCancel && (
            <Button variant="outline" type="button" onClick={onCancel}>
              {t('cancel')}
            </Button>
          )}
          <Button 
            type="submit"
            disabled={createCurrencyMutation.isPending || updateCurrencyMutation.isPending}
          >
            {createCurrencyMutation.isPending || updateCurrencyMutation.isPending
              ? t('saving')
              : currency
              ? t('updateCurrency')
              : t('createCurrency')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
